import quoteDefinitions from './quotes.js';
import { setParallaxTranslation } from './parallaxMotion.js';
import { getMobileStandalone } from './mobileStandalone.js';

const quoteModule = {
  init() {
    const container = document.querySelector('.pages-container');
    if (!container) return;
    const quotes = [];
    for (const definition of quoteDefinitions) {
      const page = Array.from(container.querySelectorAll('.page'))
        .find(element => element.dataset.id === definition.page);
      if (!page) continue;
      const mobileAnchor = getMobileStandalone(container, definition.mobile, definition.page);
      const quote = document.createElement('blockquote');
      quote.className = `quote-module quote-module--${definition.tone || 'light'}`;
      quote.dataset.quote = '';
      if (definition.color) quote.style.setProperty('--quote-color', definition.color);
      for (const property of ['top', 'bottom', 'left', 'right', 'width']) {
        if (definition[property]) quote.style.setProperty(`--quote-${property}`, definition[property]);
        if (definition.mobile?.[property] != null) {
          quote.style.setProperty(`--quote-mobile-${property}`, definition.mobile[property]);
        }
      }
      if (definition.bottom != null) quote.style.setProperty('--quote-top', 'auto');
      if (definition.right != null) quote.style.setProperty('--quote-left', 'auto');
      const mobileBottom = definition.mobile?.bottom != null ||
        (definition.bottom != null && definition.mobile?.top == null);
      const mobileRight = definition.mobile?.right != null ||
        (definition.right != null && definition.mobile?.left == null);
      quote.style.setProperty('--quote-mobile-top', mobileBottom ? 'auto' :
        (definition.mobile?.top ?? definition.top ?? '12%'));
      quote.style.setProperty('--quote-mobile-bottom', mobileBottom ?
        (definition.mobile?.bottom ?? definition.bottom) : 'auto');
      quote.style.setProperty('--quote-mobile-left', mobileRight ? 'auto' :
        (definition.mobile?.left ?? definition.left ?? '12%'));
      quote.style.setProperty('--quote-mobile-right', mobileRight ?
        (definition.mobile?.right ?? definition.right) : 'auto');
      const addMark = (paragraph, position) => {
        const image = document.createElement('img');
        image.className = `quote-module__mark quote-module__mark--${position}`;
        image.src = `./img/quote-${position}.png`;
        image.alt = '';
        image.setAttribute('aria-hidden', 'true');
        if (position === 'start') paragraph.prepend(image);
        else paragraph.append(image);
        return image;
      };
      let previousLineEndsWithMark = false;
      const endMarks = [];
      const textLines = [];
      for (const line of definition.lines) {
        const paragraph = document.createElement('p');
        paragraph.className = `quote-module__text quote-module__text--${line.size || 'medium'}`;
        if (line.size === 'attribution' && previousLineEndsWithMark) {
          paragraph.classList.add('quote-module__text--attribution-after-mark');
        }
        const content = document.createElement('span');
        content.className = 'quote-module__content';
        content.textContent = line.text.replace(/<br\s*\/?\s*>/gi, '\n');
        textLines.push({ line, content });
        paragraph.append(content);
        if (['start', 'both'].includes(line.quotationMarks)) addMark(paragraph, 'start');
        if (['end', 'both'].includes(line.quotationMarks)) {
          endMarks.push({ paragraph, content, mark: addMark(paragraph, 'end') });
        }
        quote.append(paragraph);
        previousLineEndsWithMark = ['end', 'both'].includes(line.quotationMarks);
      }
      const anchor = page.querySelector('.video-over-video-stage, .filmstrip-stage') || page;
      anchor.append(quote);
      quotes.push({ definition, page, anchor, mobileAnchor, quote, endMarks, textLines });
    }
    const desktop = matchMedia('(min-width: 768px)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    let scheduled = false;
    const update = () => {
      scheduled = false;
      const strength = reducedMotion.matches ? 0 :
        Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--quote-parallax-amount')) || 0;
      const viewport = container.getBoundingClientRect();
      for (const { definition, page, anchor, mobileAnchor, quote, endMarks, textLines } of quotes) {
        const canvas = page.classList.contains('mobile-page-pan--enabled')
          ? page.querySelector('.mobile-page-pan-canvas') : null;
        const desktopLayout = desktop.matches || Boolean(canvas);
        const targetPage = !desktopLayout && definition.mobile?.page != null
          ? Array.from(container.querySelectorAll('.page'))
            .find(element => element.dataset.id === String(definition.mobile.page)) || page
          : page;
        const standalone = !desktopLayout && mobileAnchor != null;
        if (mobileAnchor) mobileAnchor.style.display = canvas ? 'none' : '';
        const targetAnchor = canvas || (standalone ? mobileAnchor :
          targetPage.querySelector('.video-over-video-stage, .filmstrip-stage') || targetPage);
        if (quote.parentElement !== targetAnchor) targetAnchor.append(quote);
        for (const { line, content } of textLines) {
          const text = (desktopLayout ? line.text : line.mobileText ?? line.text)
            .replace(/<br\s*\/?\s*>/gi, '\n');
          if (content.textContent !== text) content.textContent = text;
        }
        const settings = desktopLayout ? definition : { ...definition, ...definition.mobile };
        if (standalone && !mobileAnchor.dataset.fixedHeight) mobileAnchor.style.height = `${quote.offsetHeight + 128}px`;
        const darkTone = settings.tone === 'dark';
        quote.classList.toggle('quote-module--dark', darkTone);
        quote.classList.toggle('quote-module--light', !darkTone);
        const bounds = targetAnchor.getBoundingClientRect();
        const distance = desktopLayout ? viewport.left - bounds.left : viewport.top - bounds.top;
        const rightAnchored = settings.right != null &&
          (desktopLayout || definition.mobile?.left == null);
        const override = settings.parallax == null ? NaN : Number(settings.parallax);
        const quoteStrength = reducedMotion.matches || standalone ? 0 : Number.isFinite(override)
          ? override : rightAnchored ? 0 : strength;
        const displacement = distance * quoteStrength;
        const offset = Number.isFinite(override) ? displacement : Math.max(-80, Math.min(80, displacement));
        if (targetPage.classList.contains('video-over-video-page--active')) {
          setParallaxTranslation(quote, 0, desktop.matches, true);
          quote.dataset.sceneFrozen = 'true';
        } else {
          quote.dataset.sceneFrozen = 'false';
          setParallaxTranslation(quote, offset, desktopLayout, reducedMotion.matches || Boolean(canvas));
        }
        for (const { paragraph, content, mark } of endMarks) {
          const range = document.createRange();
          range.selectNodeContents(content);
          const lineRects = range.getClientRects();
          if (!lineRects.length) continue;
          const finalLine = lineRects[lineRects.length - 1];
          const paragraphBounds = paragraph.getBoundingClientRect();
          const gap = Number.parseFloat(getComputedStyle(content).fontSize) * 0.1;
          mark.style.left = `${finalLine.right - paragraphBounds.left + gap}px`;
          mark.style.top = `${finalLine.top - paragraphBounds.top}px`;
        }
      }
    };
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(update);
    };
    container.addEventListener('scroll', () => {
      if (desktop.matches) schedule();
      else update();
    }, { passive: true });
    window.addEventListener('resize', schedule);
    desktop.addEventListener('change', update);
    reducedMotion.addEventListener('change', schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(container);
    for (const { page } of quotes) observer.observe(page);
    update();
  },
};

export default quoteModule;